import { Injectable, NgZone } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

export type ClientLogLevel = 'debug' | 'info' | 'warn' | 'error';

export interface ClientLogEvent {
    level: ClientLogLevel;
    message: string;
    requestId?: string;
    url?: string;
    name?: string;
    stack?: string;
    context?: Record<string, unknown>;
}

const FLUSH_INTERVAL_MS = 5000;
const MAX_BUFFER = 20;
const CLIENT_LOGS_PATH = '/client-logs';

/**
 * Buffers client-side log events and ships them in batches to the backend
 * (POST /api/client-logs), which re-emits them through the server's structured
 * logger. The browser never talks to the log store directly — everything goes
 * through the API so redaction and offline buffering stay centralised.
 *
 * Fed by GlobalErrorHandler (client-side + user-activity exceptions) and
 * LoggingInterceptor (API failures).
 */
@Injectable({ providedIn: 'root' })
export class LoggingService {
    private buffer: ClientLogEvent[] = [];
    private timer: any = null;

    constructor(private http: HttpClient, private zone: NgZone) {
        // Best-effort flush when the tab is closing.
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', () => this.flush(true));
        }
    }

    debug(message: string, event: Partial<ClientLogEvent> = {}): void { this.enqueue('debug', message, event); }
    info(message: string, event: Partial<ClientLogEvent> = {}): void { this.enqueue('info', message, event); }
    warn(message: string, event: Partial<ClientLogEvent> = {}): void { this.enqueue('warn', message, event); }
    error(message: string, event: Partial<ClientLogEvent> = {}): void { this.enqueue('error', message, event); }

    private enqueue(level: ClientLogLevel, message: string, event: Partial<ClientLogEvent>): void {
        this.buffer.push({
            level,
            message,
            url: event.url ?? (typeof window !== 'undefined' ? window.location.href : undefined),
            requestId: event.requestId,
            name: event.name,
            stack: event.stack,
            context: event.context,
        });

        if (this.buffer.length >= MAX_BUFFER) {
            this.flush();
            return;
        }
        // Schedule outside Angular so the timer doesn't keep change-detection/tests busy.
        if (!this.timer) {
            this.zone.runOutsideAngular(() => {
                this.timer = setTimeout(() => this.zone.run(() => this.flush()), FLUSH_INTERVAL_MS);
            });
        }
    }

    private flush(useBeacon = false): void {
        if (this.timer) { clearTimeout(this.timer); this.timer = null; }
        if (this.buffer.length === 0) { return; }

        const events = this.buffer;
        this.buffer = [];
        const endpoint = `${environment.apiUrl}${CLIENT_LOGS_PATH}`;
        const payload = JSON.stringify({ events });

        // On unload, sendBeacon is the only reliable transport.
        if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
            try { navigator.sendBeacon(endpoint, new Blob([payload], { type: 'application/json' })); } catch { /* ignore */ }
            return;
        }

        // Swallow errors — logging must never itself trigger the error handler (loop guard).
        this.http.post(endpoint, { events }).subscribe({ next: () => {}, error: () => {} });
    }
}
